import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  KonamiDbSet,
  Nullable,
  Set,
  UserWithToken,
  SetCardSets,
  SetOption,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';
import z from 'zod';
import axiosClient from './request-client';
import { AxiosRequestConfig } from 'axios';

export const updateSetSchema = z.object({
  collectionSize: z.number().min(1).optional(),
  rarities: z.array(z.string()).optional(),
  completed: z.boolean().optional(),
  releaseDate: z.date().nullable().optional(),
  releaseDateNA: z.date().nullable().optional(),
  releaseDateEU: z.date().nullable().optional(),
  releaseDateOC: z.date().nullable().optional(),
  correlativeCode: z.boolean().optional(),
  image: z.string().optional(),
  numberOfCards: z.number().min(1).optional(),
});

export const updateSetLinksSchema = z.object({
  yugipedia: z.url().nullable(),
  konamiDb: z.url().nullable(),
  ygoProDeck: z.url().nullable(),
  ygoWikia: z.url().nullable(),
  ygoFandom: z.url().nullable(),
});

export const updateSetLanguagesSchema = z.object({
  konamiNames_en: z.string().nullable(),
  konamiNames_fr: z.string().nullable(),
  konamiNames_de: z.string().nullable(),
  konamiNames_it: z.string().nullable(),
  konamiNames_sp: z.string().nullable(),
  konamiNames_pt: z.string().nullable(),
  codes_en: z.string().nullable(),
  codes_de: z.string().nullable(),
  codes_fr: z.string().nullable(),
  codes_it: z.string().nullable(),
  codes_sp: z.string().nullable(),
  codes_pt: z.string().nullable(),
});

export const updateSetIdsSchema = z.object({
  neuronId_en: z.string().nullable(),
  neuronId_fr: z.string().nullable(),
  neuronId_de: z.string().nullable(),
  neuronId_it: z.string().nullable(),
  neuronId_sp: z.string().nullable(),
  neuronId_pt: z.string().nullable(),
});

export const updateSetProviderIdsSchema = z.object({
  cardTraderId: z.number().nullable(),
  cardMarketId: z.number().nullable(),
  tcgCSVId: z.number().nullable(),
  cardTraderUrl: z.string().nullable(),
  cardMarketUrl: z.string().nullable(),
  tcgCSVUrl: z.string().nullable(),
});

export const updateSetParentSchema = z.object({
  parentSetId: z.number().nullable(),
});

export const updateCardSetProviderIdsSchema = z.object({
  tcgPlayerId: z.number().nullable(),
  cardTraderId: z.number().nullable(),
  cardMarketId: z.number().nullable(),
});

export const listSets = async (
  user: Nullable<UserWithToken>,
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<Set>>(
    gql`
      query listSets($filters: SetFilters) {
        data: sets(filters: $filters) {
          data {
            id
            konamiName
            code
            completed
            neuronId

            releaseDate
            releaseDates {
              region
              date
            }

            image
            numberOfCards
            collectionSize
            isOldSet

            cardTraderId
            cardMarketId
            tcgCSVId

            hasUnknownRarities
            unknownRaritiesCount
            hasCardGaps
            cardGapsCount
            hasMissingCardTraderIds
            missingCardTraderIdsCount
            hasMissingCardMarketIds
            missingCardMarketIdsCount
            hasMissingNeuronIds
            missingNeuronIdsCount
            hasMissingSetInfo
            missingSetInfoDetails
            hasMissingImages
            missingImagesCount
            selectedImagesCount
            hasAllCardImages
            hasAllCardImagesWithProviders
            cardsMissingAnyImageCount
            cardsMissingFullImageCount
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    variables
  );

export const listKonamiDbSets = async (
  user: Nullable<UserWithToken>,
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<KonamiDbSet>>(
    gql`
      query listKonamiDbSets {
        data: konamiDbSets {
          data {
            konamiName
            setId
            url
            releaseDate
            neuronId
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    variables
  );

/**
 * Buscador de sets. Version liviana de `listSets`, que trae ~30 campos por set
 * —entre ellos los contadores de alertas, que se calculan— y no sirve para un
 * combobox que dispara una query por tecla.
 */
export const searchSets = async (
  user: Nullable<UserWithToken>,
  variables: { search?: string; limit?: number }
) =>
  getGraphQLQueryClient(user).request<{
    data: { data: SetOption[] };
  }>(
    gql`
      query searchSets($filters: SetFilters) {
        data: sets(filters: $filters) {
          data {
            id
            konamiName
            code
            releaseDate
          }
        }
      }
    `,
    { filters: { search: variables.search, limit: variables.limit ?? 20 } }
  );

/**
 * Ediciones de un set, incluidas las de sus promos (`allCardSets` resuelve la
 * familia del set). Version liviana de `getSet`: sirve para elegir en lote la
 * edicion de varias cartas sin traerse el detalle entero del set.
 *
 * `konamiId` sale de `card`, no de la columna denormalizada del `CardSet`.
 */
export const getSetCardSets = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<{ data: SetCardSets | null }>(
    gql`
      query setCardSets($id: Int!) {
        data: set(id: $id) {
          id
          konamiName
          code
          allCardSets {
            id
            code
            rarity
            label
            card {
              konamiId
            }
          }
        }
      }
    `,
    { id }
  );

export const getSet = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<Set>>(
    gql`
      query ($id: Int!) {
        data: set(id: $id) {
          id
          konamiName
          completed
          neuronId

          konamiNames {
            de
            en
            fr
            it
            sp
            pt
          }

          code
          codes {
            de
            en
            fr
            it
            sp
            pt
          }

          neuronIds {
            de
            en
            fr
            it
            sp
            pt
          }

          rarities
          correlativeCode

          releaseDate
          releaseDates {
            region
            date
          }

          urls {
            konamiDb
            yugipedia
            ygoProDeck
            ygoWikia
            ygoFandom
            cardtrader
            cardmarket
            tcgcsv
          }

          image
          numberOfCards
          collectionSize
          isOldSet

          cardTraderId
          cardMarketId
          tcgCSVId

          hasUnknownRarities
          unknownRaritiesCount
          hasCardGaps
          cardGapsCount
          hasMissingCardTraderIds
          missingCardTraderIdsCount
          hasMissingCardMarketIds
          missingCardMarketIdsCount
          hasMissingNeuronIds
          missingNeuronIdsCount
          hasMissingSetInfo
          missingSetInfoDetails
          hasMissingImages
          missingImagesCount

          cardSet {
            id
            cardId
            code
            konamiId
            rarity
            label
            overframe
            alternateArt
            image
            cardTraderImage
            cardMarketImage
            tcgPlayerId
            cardTraderId
            cardMarketId

            card {
              name
              names {
                de
                en
                fr
                it
                sp
                pt
              }

              neuronId
              image
              type
            }

            inventory {
              quantity
            }
          }

          parentSetId
          parent {
            id
            konamiName
            code
          }
          children {
            id
            konamiName
            code
          }
        }
      }
    `,
    { id }
  );

export const updateSet = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: Partial<Set>
) =>
  getGraphQLQueryClient(user).request<WithData<Set>>(
    gql`
      mutation updateSet($id: Int!, $input: UpdateSetInput!) {
        data: updateSet(id: $id, input: $input) {
          id
          konamiName
          code
          releaseDate
          image
          numberOfCards
          cardTraderId
          cardMarketId
          tcgCSVId
          parentSetId
        }
      }
    `,
    { id, input }
  );

export const updateCardSet = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: {
    rarity?: string;
    image?: string | null;
    label?: string;
    tcgPlayerId?: number | null;
    cardTraderId?: number | null;
    cardMarketId?: number | null;
    overframe?: boolean;
    alternateArt?: boolean;
  }
) =>
  getGraphQLQueryClient(user).request<
    WithData<{
      id: number;
      rarity: string;
      label: string;
      image: string | null;
      tcgPlayerId: number | null;
      cardTraderId: number | null;
      cardMarketId: number | null;
      overframe: boolean;
      alternateArt: boolean;
    }>
  >(
    gql`
      mutation updateCardSet($id: Int!, $input: UpdateCardSetInput!) {
        data: updateCardSet(id: $id, input: $input) {
          id
          rarity
          label
          image
          tcgPlayerId
          cardTraderId
          cardMarketId
          overframe
          alternateArt
        }
      }
    `,
    { id, input }
  );

export const batchUpdateCardSetImages = async (
  user: Nullable<UserWithToken>,
  input: { id: number; image: string }[]
) =>
  getGraphQLQueryClient(user).request<WithData<number>>(
    gql`
      mutation batchUpdateCardSetImages(
        $input: [BatchUpdateCardSetImageInput!]!
      ) {
        data: batchUpdateCardSetImages(input: $input)
      }
    `,
    { input }
  );

export const getSetReview = async (user: Nullable<UserWithToken>, setId: number) =>
  getGraphQLQueryClient(user).request(
    gql`
      query SetReview($setId: Int!) {
        data: setReview(setId: $setId) {
          set { id code konamiName cardTraderId cardMarketId tcgCSVId }
          rows {
            cardSet {
              id code konamiId rarity label
              cardTraderId cardMarketId tcgPlayerId
              image cardTraderImage cardMarketImage tcgPlayerImage
              card { id name }
            }
            konamiImage
            issues { provider category ambiguousCandidates ambiguousLabels expectedId message }
            isComplete
          }
          summary { total complete withIssues }
        }
      }
    `,
    { setId }
  );

/**
 * Sube la imagen de la expansión (sólo admins).
 *
 * Es REST y no una mutation porque va el archivo: `/graphql` sólo acepta JSON.
 * Reemplaza a la del provider, que matchea por código y por eso es la misma
 * para dos expansiones que comparten código.
 */
export const uploadSetImage = (
  setId: number,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('image', file);

  return axiosClient.post(`/sets/${setId}/image`, formData, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

/** Borra la imagen propia de la expansión y la deja sin imagen. */
export const deleteSetImage = (setId: number, config?: AxiosRequestConfig) =>
  axiosClient.delete(`/sets/${setId}/image`, config);

/**
 * Una carta de un set tal como la muestra una landing pública.
 *
 * `code` y `rarity` pueden venir vacíos: las cartas de un set que todavía no
 * salió se importan de YGOProDeck apenas se anuncian, antes de que Konami
 * publique los códigos.
 */
export type PublicSetCard = {
  id: number;
  code: string;
  rarity: string;
  card: {
    id: number;
    konamiId: number;
    name: string;
    names: { en?: string | null; sp?: string | null } | null;
    image: string | null;
    type: string | null;
    frameType: string | null;
  };
};

/** Las cartas de un set, sin sesión. Ver `publicSetCards` en el API. */
export const getPublicSetCards = async (
  user: Nullable<UserWithToken>,
  setId: number
) =>
  getGraphQLQueryClient(user).request<{ data: PublicSetCard[] }>(
    gql`
      query publicSetCards($setId: Int!) {
        data: publicSetCards(setId: $setId) {
          id
          code
          rarity
          card {
            id
            konamiId
            name
            names {
              en
              sp
            }
            image
            type
            frameType
          }
        }
      }
    `,
    { setId }
  );
